/* File: globalCalling.jsx
 * Path: ananunadm/src/helpers/globalCalling.jsx
 *
 * Provides call state and modals at the app root so incoming call notifications
 * work regardless of which page the user is on.
 *
 * Handles:
 * - 1-on-1 calls (PeerJS, socket events: callUser / answerCall / endCall)
 * - Conference calls (WebRTC mesh, socket events: call:ring / call:* namespace)
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { Modal, Stack, Title, Text, Group, Button, TextInput } from "@mantine/core";
import { IconPhone, IconPhoneOff, IconUsers, IconLock } from "@tabler/icons-react";
import { useSocket } from "./socket";
import { useUser } from "./userContext";
import { Calling } from "../components/chat/calling";
import { ConferenceCalling } from "../components/chat/conferenceCalling";
import API from "./api";
import config from "../config";
import { getAnyChatFeatureAccess } from "./chatPermissionUtils";

const GlobalCallingContext = createContext(null);

export function useGlobalCalling() {
	return useContext(GlobalCallingContext);
}

export function GlobalCallingProvider({ lang, children }) {
	const { socket } = useSocket();
	const { user } = useUser();
	const incomingAudioAccess = getAnyChatFeatureAccess(user, ["call.audio.start", "calling"], {
		defaultValue: false
	});
	const incomingVideoAccess = getAnyChatFeatureAccess(user, ["call.video.start", "videoCalling"], {
		defaultValue: false
	});
	const conferenceJoinAccess = getAnyChatFeatureAccess(
		user,
		["call.join", "call.conference.start", "calling", "videoCalling"],
		{ defaultValue: false }
	);
	const canViewIncomingAudioCall = incomingAudioAccess.read;
	const canAnswerIncomingAudioCall = incomingAudioAccess.update;
	const canViewIncomingVideoCall = incomingVideoAccess.read;
	const canAnswerIncomingVideoCall = incomingVideoAccess.update;
	const canViewIncomingConference = conferenceJoinAccess.read;
	const canJoinIncomingConference = conferenceJoinAccess.update;

	// ─── 1-on-1 call state ──────────────────────────────────────────────────────

	const [callType, setCallType] = useState(null);
	const [caller, setCaller] = useState(null);
	const [incomingCall, setIncomingCall] = useState(false);
	const [incomingCallType, setIncomingCallType] = useState(null);
	const [incomingCallId, setIncomingCallId] = useState(null);
	const [incomingCallSignal, setIncomingCallSignal] = useState(null);
	const [partner, setPartner] = useState(null);
	const [chatId, setChatId] = useState(null);

	// ─── Conference call state ───────────────────────────────────────────────────

	const [incomingConference, setIncomingConference] = useState(null); // { roomId, callType, callerName, callerImage, chatId, hasPassword }
	const [activeConference, setActiveConference] = useState(null); // { roomId, callType, participants }
	const [conferenceMinimized, setConferenceMinimized] = useState(false);
	const [conferencePassword, setConferencePassword] = useState('');
	const [conferencePasswordError, setConferencePasswordError] = useState('');

	const audioRef = useRef(new Audio("/assets/call.mp3"));

	const playAudio = () => {
		audioRef.current.loop = true;
		audioRef.current.play().catch(() => {});
	};

	const stopAudio = () => {
		audioRef.current.pause();
		audioRef.current.currentTime = 0;
	};

	// ─── Fetch user by ID ────────────────────────────────────────────────────────

	const fetchUser = async (userId) => {
		try {
			const res = await API.get({
				route: config.api.system.user,
				query: { _id: userId },
				uploadType: { destiny: "image" }
			});
			const list = Array.isArray(res)
				? res
				: Array.isArray(res?.items)
					? res.items
					: Array.isArray(res?.data)
						? res.data
						: res
							? [res]
							: [];
			const found = Array.isArray(list) ? list.find((u) => String(u._id) === String(userId)) : list;
			return found || null;
		} catch (_) {
			return null;
		}
	};

    const outgoingIntentRef = useRef(null);
    useEffect(() => { if (!callType) outgoingIntentRef.current = null; }, [callType]);
	// ─── 1-on-1 incoming call listener ──────────────────────────────────────────

	useEffect(() => {
		if (!socket) return;

		const handleCallUser = async (data) => {
            const intent = outgoingIntentRef.current;
            if (intent?.type === 'audio' && data.callType === 'audio' && String(intent.peerId) === String(data.from)) return;
			const isVideoCall = data?.callType === "video";
			const canViewIncomingCall = isVideoCall
				? canViewIncomingVideoCall
				: canViewIncomingAudioCall;
			if (!canViewIncomingCall) return;
			const socketCaller = data?.caller || {
				_id: data.from,
				name: data.name || null,
				uploads: data.callerUploads || []
			};
			setPartner(socketCaller);
			setCaller(data.from);
			setIncomingCallId(data.callId || null);
			setIncomingCallSignal(data.signal || null);
			setChatId(data.chatId || null);
			setIncomingCallType(data.callType);
			setIncomingCall(true);

			const callerUser = await fetchUser(data.from);
			if (callerUser) {
				setPartner((current) => {
					if (String(current?._id || "") !== String(data.from || "")) return current;
					return {
						...current,
						...callerUser,
						uploads: callerUser.uploads?.length ? callerUser.uploads : current?.uploads
					};
				});
			}
		};

		socket.on("callUser", handleCallUser);
		return () => socket.off("callUser", handleCallUser);
	}, [socket, canViewIncomingAudioCall, canViewIncomingVideoCall]);

	// ─── Conference incoming call listener (call:ring) ───────────────────────────

	useEffect(() => {
		if (!socket) return;

		const handleCallRing = (data) => {
			// { roomId, callType, startedBy, callerName, callerImage, chatId }
			if (activeConference) return; // already in a call
			if (!canViewIncomingConference) return;
			setIncomingConference(data);
			playAudio();
		};

		const handleCallCancelled = ({ roomId }) => {
			setIncomingConference((prev) => {
				if (prev?.roomId === roomId) {
					stopAudio();
					return null;
				}
				return prev;
			});
		};

		const handleCallEnded = ({ roomId }) => {
			setIncomingConference((prev) => {
				if (prev?.roomId === roomId) {
					stopAudio();
					return null;
				}
				return prev;
			});
			setActiveConference((prev) => {
				if (prev?.roomId === roomId) return null;
				return prev;
			});
			setConferenceMinimized(false);
		};

		const handleCallMissed = ({ roomId }) => {
			setIncomingConference((prev) => {
				if (prev?.roomId === roomId) {
					stopAudio();
					return null;
				}
				return prev;
			});
		};

		// Dismiss incoming conference modal when the user accepted on another device
		const handleAnsweredElsewhere = ({ roomId }) => {
			setIncomingConference((prev) => {
				if (!prev || prev.roomId !== roomId) return prev;
				stopAudio();
				return null;
			});
		};

		socket.on("call:ring", handleCallRing);
		socket.on("call:cancelled", handleCallCancelled);
		socket.on("call:ended", handleCallEnded);
		socket.on("call:missed", handleCallMissed);
		socket.on("call:answered-elsewhere", handleAnsweredElsewhere);

		return () => {
			socket.off("call:ring", handleCallRing);
			socket.off("call:cancelled", handleCallCancelled);
			socket.off("call:ended", handleCallEnded);
			socket.off("call:missed", handleCallMissed);
			socket.off("call:answered-elsewhere", handleAnsweredElsewhere);
		};
	}, [socket, activeConference, canViewIncomingConference]);

	// ─── Accept/reject conference call ──────────────────────────────────────────

	const acceptConference = useCallback(async () => {
		if (!incomingConference || !socket) return;
		const { roomId, callType: cType, chatId: cChatId } = incomingConference;

		const result = await new Promise((resolve) => socket.emit("call:accept", { roomId, password: conferencePassword || undefined }, resolve));
		if (!result?.ok) {
			setConferencePasswordError(result?.error === 'Wrong password' ? 'Incorrect password' : (result?.error || 'Failed to join'));
			return;
		}
		stopAudio();
		setActiveConference({ roomId, callType: cType, chatId: cChatId || null, participants: result.participants || [], isHost: false });
		setIncomingConference(null);
		setConferencePassword('');
		setConferencePasswordError('');
	}, [incomingConference, socket, conferencePassword]);

	const rejectConference = useCallback(() => {
		if (!incomingConference || !socket) return;
		stopAudio();
		socket.emit("call:reject", { roomId: incomingConference.roomId });
		setIncomingConference(null);
		setConferencePassword('');
		setConferencePasswordError('');
	}, [incomingConference, socket]);

	// ─── Start call (called from chat.jsx) ──────────────────────────────────────

	const startCall = (partnerUser, type, targetChatId = null) => {
        outgoingIntentRef.current = { peerId: partnerUser._id, type };
        setCaller(null);
		setPartner(partnerUser);
		setChatId(targetChatId);
		setCallType(type);
	};

	// Start a conference call
	const startConference = useCallback(async ({ callType: cType, invitedUsers = [], chatId: cChatId = null, password, groupMembers = [] }) => {
		if (!socket) { console.warn('[startConference] No socket available'); return false; }
		if (!user?._id) { console.warn('[startConference] No user available'); return false; }
		const roomId = `conf_${user._id}_${Date.now()}`;
		const result = await new Promise((resolve) => {
			socket.emit("call:start", { callType: cType, chatId: cChatId, invitedUsers, roomId, password: password || undefined }, resolve);
		});
		if (result?.ok) {
			setActiveConference({ roomId, callType: cType, chatId: cChatId || null, participants: [], groupMembers, isHost: true });
		} else {
			console.warn('[startConference] call:start failed:', result);
		}
		return Boolean(result?.ok);
	}, [socket, user?._id]);

	// ─── Render ──────────────────────────────────────────────────────────────────

	const isConferenceType = (ct) => ct === 'conference_audio' || ct === 'conference_video';

	const isCallActive = Boolean(
		callType ||
		incomingCall ||
		incomingCallType ||
		incomingCallId ||
		incomingConference ||
		activeConference
	);

	return (
		<GlobalCallingContext.Provider value={{ startCall, startConference, isCallActive, activeConference, incomingConference }}>
			{children}

			{/* 1-on-1 calling component */}
			{user && socket && !isConferenceType(callType) && !isConferenceType(incomingCallType) && (
				<Calling
					partner={partner}
					socket={socket}
					user={user}
					lang={lang}
					callType={callType}
					setCallType={setCallType}
					incomingCall={incomingCall}
					setIncomingCall={setIncomingCall}
					incomingCallType={incomingCallType}
					setIncomingCallType={setIncomingCallType}
					incomingCallId={incomingCallId}
					setIncomingCallId={setIncomingCallId}
					incomingCallSignal={incomingCallSignal}
					setIncomingCallSignal={setIncomingCallSignal}
					caller={caller}
					chatId={chatId}
					setChatId={setChatId}
					canViewIncomingAudioCall={canViewIncomingAudioCall}
					canAnswerIncomingAudioCall={canAnswerIncomingAudioCall}
					canViewIncomingVideoCall={canViewIncomingVideoCall}
					canAnswerIncomingVideoCall={canAnswerIncomingVideoCall}
				/>
			)}

			{/* Incoming conference call modal */}
			{incomingConference && !activeConference && canViewIncomingConference && (
				<Modal
					opened
					centered
					withCloseButton={false}
					onClose={rejectConference}
					size="sm"
					overlayProps={{ opacity: 0.6, blur: 3 }}
				>
					<Stack align="center" spacing="lg" p="md">
						<IconUsers size={48} color="#4dabf7" />
						<Title order={3}>
							Incoming {incomingConference.callType === 'conference_video' ? 'Video' : 'Audio'} Conference
						</Title>
						<Text size="md">From: {(typeof incomingConference.callerName === 'object' ? incomingConference.callerName?.[lang] : incomingConference.callerName) || 'Unknown'}</Text>
						{incomingConference.hasPassword && (
							<TextInput
								w="100%"
								placeholder="Enter call password"
								type="password"
								icon={<IconLock size={16} />}
								value={conferencePassword}
								onChange={(e) => { setConferencePassword(e.currentTarget.value); setConferencePasswordError(''); }}
								error={conferencePasswordError || undefined}
								onKeyDown={(e) => { if (e.key === 'Enter') acceptConference(); }}
							/>
						)}
						<Group position="center" mt="md">
							<Button
								onClick={acceptConference}
								color="green"
								size="lg"
								leftIcon={<IconPhone size={20} />}
								disabled={!canJoinIncomingConference}
							>
								Join
							</Button>
							<Button onClick={rejectConference} color="red" size="lg" leftIcon={<IconPhoneOff size={20} />}>
								Decline
							</Button>
						</Group>
					</Stack>
				</Modal>
			)}

			{/* Active conference calling component */}
			{activeConference && user && socket && (
				<ConferenceCalling
					roomId={activeConference.roomId}
					callType={activeConference.callType}
					socket={socket}
					user={user}
					lang={lang}
					initialParticipants={activeConference.participants || []}
					chatId={activeConference.chatId}
					initialGroupMembers={activeConference.groupMembers || []}
					isHost={activeConference.isHost === true}
					isMinimized={conferenceMinimized}
					onMinimize={setConferenceMinimized}
					onEnded={() => {
						setActiveConference(null);
						setConferenceMinimized(false);
					}}
				/>
			)}
		</GlobalCallingContext.Provider>
	);
}
